import type { Article } from './articleTypes';

export const citadine15000: Article = {
  slug: 'quelle-citadine-15000-euros',
  category: 'Budget',
  title: 'Quelle citadine d’occasion choisir avec 15 000 € ?',
  seoTitle: 'Quelle citadine pour 15 000 € ? 5 choix en 2026',
  description:
    'Clio, Yaris, Swift, Fabia ou Sandero : cinq citadines d’occasion à comparer avec 15 000 €, selon vos trajets et vos vrais besoins.',
  intro:
    'À ce niveau de budget, il y a assez de choix pour devenir difficile. Le bon achat n’est pas forcément la citadine la plus récente, la moins kilométrée ou la mieux équipée : c’est celle dont les compromis correspondent à vos trajets.',
  image: '/assets/guides/guide-citadine-15000.webp',
  imageAlt:
    'Citadine bleue garée sur une aire minérale, devant une ligne de direction bleue',
  publishedAt: '2026-07-17',
  updatedAt: '2026-07-17',
  readingTime: '12 min',
  takeaways: [
    'Pour un usage mixte, la Clio V TCe 90 et la Fabia IV 1.0 TSI sont les deux pistes les plus polyvalentes.',
    'Pour une majorité de ville et une boîte automatique, la Yaris hybride mérite le surcoût — à condition d’accepter un véhicule souvent plus kilométré.',
    'Gardez une réserve après l’achat : un budget de 15 000 € ne devrait pas être dépensé jusqu’au dernier euro dans l’annonce.',
  ],
  sections: [
    {
      id: 'budget-reel',
      title: 'Commencer par définir les 15 000 €',
      content: (
        <>
          <p>
            Un plafond de 15 000 € peut désigner le prix de la voiture ou l’enveloppe complète.
            Ce n’est pas la même recherche. À la voiture peuvent encore s’ajouter la carte grise,
            un déplacement, une première révision, deux pneus ou une assurance plus chère que
            prévu. Le montant de la carte grise dépend notamment de la région et de la puissance
            fiscale : je préfère donc le calculer pour le véhicule précis plutôt que de l’intégrer
            dans une moyenne trompeuse.
          </p>
          <aside className="as-article-note as-note-estimate">
            <strong>Mon hypothèse de travail</strong>
            <p>
              Si 15 000 € est votre enveloppe totale, je cible d’abord des annonces entre 13 000 et
              14 000 €. La réserve de 1 000 à 2 000 € n’est pas une dépense certaine : elle évite
              simplement qu’un entretien immédiat ou des pneus fatigués mette le budget sous tension.
            </p>
          </aside>
          <p>
            Le marché observé le 17 juillet 2026 montre une offre abondante de Clio V, de Swift,
            de Fabia IV et de Sandero III dans cette zone de prix. La Yaris IV hybride y entre aussi,
            mais l’arbitrage se fait plus souvent sur le kilométrage ou la finition. Ce sont des prix
            affichés, pas des prix de transaction, et une annonce isolée ne constitue jamais une cote.
          </p>
        </>
      ),
    },
    {
      id: 'shortlist',
      title: 'Les cinq pistes que je comparerais',
      content: (
        <>
          <p>
            Les millésimes et kilométrages ci-dessous sont des <strong>repères de recherche</strong>,
            établis à partir des annonces disponibles à la date de consultation. Une voiture plus
            ancienne avec un historique limpide peut être préférable à une occasion récente mal suivie.
          </p>
          <div className="as-table-scroll" role="region" aria-label="Comparaison de cinq citadines" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>Modèle ciblé</th>
                  <th>Repère à 15 000 €</th>
                  <th>Elle a du sens si…</th>
                  <th>Compromis principal</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Renault Clio V TCe 90</th>
                  <td>2021–2024 · env. 25 000 à 70 000 km</td>
                  <td>Vous voulez une citadine à l’aise partout et facile à retrouver.</td>
                  <td>L’état et l’équipement varient beaucoup d’un exemplaire à l’autre.</td>
                </tr>
                <tr>
                  <th>Toyota Yaris IV Hybrid 116</th>
                  <td>2020–2022 · env. 50 000 à 100 000 km</td>
                  <td>Vos trajets sont surtout urbains ou périurbains et vous voulez une automatique.</td>
                  <td>Moins reposante sur autoroute et budget souvent serré pour un bel exemplaire.</td>
                </tr>
                <tr>
                  <th>Suzuki Swift 1.2 Dualjet</th>
                  <td>2022–2024 · env. 20 000 à 70 000 km</td>
                  <td>Vous privilégiez la légèreté, la simplicité et un petit gabarit.</td>
                  <td>Insonorisation et présentation plus modestes.</td>
                </tr>
                <tr>
                  <th>Škoda Fabia IV 1.0 TSI 95</th>
                  <td>2022–2023 · env. 35 000 à 80 000 km</td>
                  <td>La citadine doit aussi faire de la route et transporter correctement.</td>
                  <td>Gabarit plus long ; les versions automatiques dépassent plus vite le budget.</td>
                </tr>
                <tr>
                  <th>Dacia Sandero III TCe 90 ou ECO-G</th>
                  <td>2022–2024 · env. 15 000 à 60 000 km</td>
                  <td>Vous voulez surtout une voiture récente, spacieuse et lisible à l’usage.</td>
                  <td>Finition simple et aides à la conduite moins complètes sur les premiers millésimes.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="as-article-caption">
            Estimations Auto Sens à partir d’un relevé d’annonces nationales. Elles ne valent ni
            cote officielle, ni promesse de disponibilité.
          </p>
        </>
      ),
    },
    {
      id: 'clio-fabia',
      title: 'Clio ou Fabia : les deux choix polyvalents',
      content: (
        <>
          <h3>Renault Clio V TCe 90 : le choix facile à ajuster</h3>
          <p>
            La Clio V n’est pas ici par réflexe national. Elle est là parce que son abondance donne
            de la latitude : couleur, finition, kilométrage, vendeur professionnel ou particulier.
            Au relevé, le moteur TCe 90 apparaît sur des exemplaires 2021 à 2024 bien placés sous
            15 000 €. Cette profondeur de marché facilite aussi la comparaison entre plusieurs voitures
            avant de se déplacer.
          </p>
          <p>
            Je viserais une version essence manuelle si l’usage est mixte. Je vérifierais surtout la
            cohérence de l’historique, l’état des pneus, le fonctionnement de l’écran et la présence
            réelle des aides annoncées. Euro NCAP signale que certaines Clio produites pendant la
            pénurie de semi-conducteurs ont été livrées sans le radar nécessaire à certaines fonctions
            de freinage automatique : le niveau d’équipement doit donc être contrôlé voiture par voiture,
            pas déduit du nom de la finition.
          </p>
          <h3>Škoda Fabia IV 1.0 TSI 95 : la petite qui fait bien la grande</h3>
          <p>
            La Fabia est la plus routière de cette sélection par son format et son habitabilité. Elle a
            du sens si la voiture sert la semaine en ville mais doit avaler régulièrement de l’autoroute,
            des bagages ou un siège enfant. Le 1.0 TSI 95 en boîte manuelle est la cible rationnelle ; le
            110 ch ou la DSG peuvent être agréables, mais ils ne doivent pas pousser à sacrifier
            l’historique ou les pneus pour rester sous le plafond.
          </p>
          <p>
            Son défaut n’est pas caché : elle mesure davantage qu’une Swift ou une Yaris. Avant de la
            retenir, je mesure la place de stationnement réelle et je fais essayer l’accès arrière aux
            personnes qui l’utiliseront. Une fiche technique ne remplace pas ce test très concret.
          </p>
        </>
      ),
    },
    {
      id: 'yaris-swift-sandero',
      title: 'Yaris, Swift ou Sandero : trois arbitrages plus tranchés',
      content: (
        <>
          <h3>Toyota Yaris Hybrid 116 : excellente en ville, pas universelle</h3>
          <p>
            La Yaris hybride est convaincante dans les ralentissements, les enchaînements de ronds-points
            et les petits trajets répétés. Sa transmission automatique et sa capacité à couper souvent le
            moteur thermique apportent un vrai confort urbain. À 15 000 €, il faut cependant accepter de
            chercher : les Yaris IV les mieux placées ont souvent davantage de kilomètres que les Clio ou
            Swift du même prix.
          </p>
          <p>
            Je demanderais les factures d’entretien et, lorsqu’il existe, le résultat d’un contrôle du
            système hybride. Je ferais aussi un vrai essai à 110 ou 130 km/h. Une voiture très agréable en
            ville peut décevoir si l’usage réel comprend chaque semaine plusieurs heures d’autoroute.
            La Yaris testée par Euro NCAP en 2020 avait obtenu cinq étoiles selon le protocole de cette année-là ;
            ce résultat doit être lu avec sa date, car les protocoles évoluent.
          </p>
          <h3>Suzuki Swift 1.2 Dualjet : la sobriété par la légèreté</h3>
          <p>
            La Swift est petite dehors, convenable dedans et généralement disponible plus récente à budget
            égal. Son appellation « Hybrid » désigne, selon la version, une micro-hybridation : elle assiste
            le moteur mais ne transforme pas la Swift en Yaris capable de se déplacer régulièrement moteur
            thermique coupé. Cette distinction évite de payer un mot sans comprendre la technologie.
          </p>
          <p>
            Je la retiendrais pour un conducteur qui veut une mécanique lisible, se gare souvent et accepte
            une ambiance moins cossue. Sur l’essai, l’attention va au bruit aérodynamique, au confort sur
            mauvaise route et à la stabilité du freinage. Ce sont des compromis d’usage, pas des défauts
            cachés.
          </p>
          <h3>Dacia Sandero III : maximiser l’âge et l’espace</h3>
          <p>
            La Sandero permet d’acheter un exemplaire récent sans consommer tout le budget. Le TCe 90 convient
            à un usage polyvalent ; l’ECO-G peut devenir pertinent si une station GPL se trouve réellement sur
            vos parcours. Là encore, le prix au kilomètre ne suffit pas : il faut intégrer le détour, la place
            prise par le réservoir et votre tolérance à cette organisation.
          </p>
          <p>
            Son résultat Euro NCAP 2021 de deux étoiles est fortement pénalisé par les aides à la conduite,
            notamment l’absence de détection des piétons et cyclistes par le freinage automatique testé. Il
            ne faut ni l’ignorer, ni le résumer à « voiture dangereuse ». Je compare les scores détaillés,
            l’équipement exact et les priorités de l’acheteur.
          </p>
        </>
      ),
    },
    {
      id: 'selon-usage',
      title: 'Mon choix selon quatre usages réels',
      content: (
        <>
          <dl className="as-article-decisions">
            <div>
              <dt>80 % de ville, boîte automatique souhaitée</dt>
              <dd>Yaris IV hybride, puis Clio E-Tech si une bonne annonce entre dans le budget.</dd>
            </div>
            <div>
              <dt>Ville la semaine, autoroute le week-end</dt>
              <dd>Clio V TCe 90 ou Fabia IV 1.0 TSI 95.</dd>
            </div>
            <div>
              <dt>Petit gabarit et dépenses contenues</dt>
              <dd>Swift 1.2 Dualjet, à condition d’accepter son isolation plus légère.</dd>
            </div>
            <div>
              <dt>Voiture récente, simple et logeable</dt>
              <dd>Sandero III TCe 90 ; ECO-G seulement si le GPL s’intègre naturellement aux trajets.</dd>
            </div>
          </dl>
          <aside className="as-article-note as-note-opinion">
            <strong>Mon avis</strong>
            <p>
              Sans autre information, je ne désigne pas une gagnante. Je garde d’abord la Clio et la
              Fabia pour leur polyvalence, puis je fais remonter la Yaris, la Swift ou la Sandero dès que
              l’usage devient plus spécifique. C’est précisément le rôle d’un cahier des charges.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: 'avant-achat',
      title: 'Ce que je contrôlerais avant de signer',
      content: (
        <>
          <ul className="as-article-checklist">
            <li>Le rapport HistoVec partagé par le vendeur et la cohérence des kilométrages.</li>
            <li>Les factures, plus informatives qu’un carnet tamponné sans détail.</li>
            <li>La date et l’état relevé au contrôle technique lorsque le véhicule y est soumis.</li>
            <li>Quatre pneus cohérents, sans usure irrégulière, et leur date de fabrication.</li>
            <li>Un démarrage à froid, un essai sur plusieurs types de route et tous les équipements.</li>
            <li>Un devis d’assurance obtenu avec la version et l’immatriculation exactes.</li>
          </ul>
          <p>
            Ces contrôles ne constituent pas une inspection mécanique. La méthode complète est détaillée
            dans le guide <a href="/conseils/verifier-voiture-occasion/">des vérifications avant l’achat
            d’une voiture d’occasion</a>. Pour choisir entre essence et hybride, le guide sur les
            <a href="/conseils/essence-diesel-hybride-electrique/"> motorisations selon l’usage</a> pose
            le calcul sans raccourci.
          </p>
        </>
      ),
    },
  ],
  sources: [
    {
      title: 'Renault Clio V d’occasion',
      publisher: 'La Centrale',
      url: 'https://www.lacentrale.fr/voiture-occasion-renault-clio-clio%2B5.html',
    },
    {
      title: 'Toyota Yaris hybride d’occasion',
      publisher: 'La Centrale',
      url: 'https://www.lacentrale.fr/voiture-occasion/hybride/modele-toyota-yaris.html',
    },
    {
      title: 'Suzuki Swift d’occasion',
      publisher: 'La Centrale',
      url: 'https://www.lacentrale.fr/occasion-voiture-modele-suzuki-swift.html',
    },
    {
      title: 'Škoda Fabia IV d’occasion',
      publisher: 'La Centrale',
      url: 'https://www.lacentrale.fr/voiture-occasion-skoda-fabia-fabia%2B4.html',
    },
    {
      title: 'Dacia Sandero III d’occasion',
      publisher: 'La Centrale',
      url: 'https://www.lacentrale.fr/voiture-occasion-dacia-sandero-sandero%2B3.html',
    },
    {
      title: 'Renault Clio — évaluation 2019 et historique de validité',
      publisher: 'Euro NCAP',
      url: 'https://www.euroncap.com/assessments/renault/clio/0753/',
    },
    {
      title: 'Toyota Yaris — évaluation 2020',
      publisher: 'Euro NCAP',
      url: 'https://www.euroncap.com/assessments/toyota/yaris/0821/',
    },
    {
      title: 'Dacia Sandero Stepway — évaluation 2021',
      publisher: 'Euro NCAP',
      url: 'https://www.euroncap.com/assessments/dacia/sandero%2Bstepway/0861/',
    },
  ],
  faqs: [
    {
      question: 'Faut-il acheter une citadine neuve ou d’occasion avec 15 000 € ?',
      answer:
        'À ce budget, l’occasion ouvre un choix nettement plus large de motorisations et d’équipements. Le neuf peut garder du sens si la simplicité de la garantie et un coût connu priment, mais il faut comparer le modèle réellement disponible et non un prix d’appel.',
    },
    {
      question: 'Quel kilométrage maximum accepter ?',
      answer:
        'Il n’existe pas de seuil universel. Un historique complet, un usage routier et un entretien suivi peuvent rendre une voiture de 90 000 km préférable à une autre de 45 000 km mal documentée. Le kilométrage doit être rapproché de l’âge, de l’usage et du prix.',
    },
    {
      question: 'Quelle citadine automatique viser à 15 000 € ?',
      answer:
        'La Toyota Yaris hybride est la piste la plus naturelle pour un usage urbain. Une Honda Jazz ou une Clio E-Tech peut aussi apparaître, souvent avec un arbitrage plus marqué sur l’âge, le kilométrage ou le dépassement du budget.',
    },
  ],
  relatedSlugs: [
    'essence-diesel-hybride-electrique',
    'verifier-voiture-occasion',
    'achat-credit-loa-lld',
  ],
};

